import React, { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import {
    Camera,
    Heart,
    Users,
    Sparkles,
    Sun,
    Compass,
    Layers,
    Smile,
    Calendar,
    SmilePlus,
    Image as ImageIcon,
    Gift
} from 'lucide-react';

const loveServices = [
    { id: '01', icon: Heart, title: 'Intimate Engagement Sessions', desc: 'Capturing your unique relationship rhythm in comfortable, deeply meaningful locations with unforced, cinematic storytelling.' },
    { id: '02', icon: Compass, title: 'Adventure & Destination Elopements', desc: 'Breathtaking landscape integration tracking your love story across rugged mountain passes, coastal cliffs, or urban skylines.' },
    { id: '03', icon: Sun, title: 'Golden Hour Couples Portraits', desc: 'Harnessing the soft, warm, atmospheric directional light of dusk to wrap your connection in an ethereal, timeless glow.' },
    { id: '04', icon: Users, title: 'Candid Family Connection Fields', desc: 'Moving away from stiff alignment to document raw, chaotic, beautiful family interactions, real laughter, and genuine embraces.' },
    { id: '05', icon: Smile, title: 'Day-In-The-Life Documentaries', desc: 'A relaxed, photojournalistic approach spending a morning or afternoon capturing your family’s real weekend rituals at love-and-family.' },
    { id: '06', icon: Gift, title: 'Anniversary & Legacy Tributes', desc: 'Honoring decades of shared history with elegant, soulful portraits that celebrate the beautiful endurance of long-term love.' },
];

const heirloomServices = [
    { id: '01', icon: SmilePlus, title: 'Play-Driven Children Sessions', desc: ' ditching the phrase "say cheese" for dynamic, interactive games that reveal your child\'s true personality and unforced smiles.' },
    { id: '02', icon: Calendar, title: 'Annual Family Heritage Collections', desc: 'A yearly structural preservation rhythm ensuring your growing family\'s evolving dynamics are documented consistently.' },
    { id: '03', icon: Layers, title: 'Bespoke Fine-Art Wall Curations', desc: 'Designing custom physical mockups to translate your digital legacy gallery into perfectly balanced, museum-grade wall art frames.' },
    { id: '04', icon: ImageIcon, title: 'Hand-Bound Fine Art Folios', desc: 'Crafting stunning, heavy-matte archival albums encased in premium natural fibers, custom-tailored to anchor your living room.' },
    { id: '05', icon: Camera, title: 'Surprise Proposal Secret Capture', desc: 'Meticulously planned, stealthy telephoto coverage mapping positions and timing perfectly to lock down that explosive "Yes!".' },
];

const relationshipDeliverables = [
    { id: '01', icon: Heart, title: 'Chemistry-Driven Action Prompts', desc: 'Utilizing warm, interactive movement frameworks rather than frozen, awkward positions to evoke natural laughter.' },
    { id: '02', icon: Sun, title: 'Dynamic Weather Flexibility', desc: 'Expert tracking of meteorological conditions, providing fluid rescheduling windows to capture perfect lighting states.' },
    { id: '03', icon: Sparkles, title: 'Timeless True-Color Color Metrics', desc: 'Avoiding heavy, fleeting trendy filters to deliver balanced, natural tones that stand up beautifully for generations.' },
    { id: '04', icon: Users, title: 'Multi-Generational Wardrobe Styling', desc: 'Comprehensive digital styling guides to help coordinate flattering textures and complementary tones for the whole group.' },
    { id: '05', icon: Smile, title: 'Meltdown-Buffered Timelines', desc: 'Extended time slots explicitly designed to accommodate toddler snacks, outfit changes, and spontaneous mood shifts safely.' },
    { id: '06', icon: Compass, title: 'Location Scouting Mapping', desc: 'Providing highly detailed, custom geolocation blueprints mapping parking, footpaths, and hidden backdrops.' },
    { id: '07', icon: Layers, title: 'Giclée Fine-Art Master Proofing', desc: 'Individually inspecting final files through precise monitor calibrations optimized for deep, soft ink transitions.' },
    { id: '08', icon: Gift, title: 'Private Digital Sharing Hubs', desc: 'Elegant, rapid-loading cloud galleries designed for immediate text deployment and high-resolution downloads for global families.' },
    { id: '09', icon: ImageIcon, title: 'Permanent Cloud-Vault Protection', desc: 'Securing your relationship landmarks inside redundant, lifelong digital archives against accidental device losses.' },
];

const LoveandFamily = () => {
    const viewportRef = useRef(null);
    const leftStripRef = useRef(null);
    const rightStripRef = useRef(null);

    useEffect(() => {
        const viewport = viewportRef.current;
        const leftStrip = leftStripRef.current;
        const rightStrip = rightStripRef.current;

        if (!viewport || !leftStrip || !rightStrip) return;

        let currentPosLeft = 0;
        let currentPosRight = 0;
        let isHovered = false;
        let animationFrameId;

        const speedLeft = 0.55;
        const speedRight = 0.35;

        const handleMouseEnter = () => { isHovered = true; };
        const handleMouseLeave = () => { isHovered = false; };

        viewport.addEventListener('mouseenter', handleMouseEnter);
        viewport.addEventListener('mouseleave', handleMouseLeave);

        function performAutoScroll() {
            if (!isHovered && leftStrip && rightStrip) {
                currentPosLeft -= speedLeft;
                currentPosRight -= speedRight;

                if (Math.abs(currentPosLeft) >= (leftStrip.scrollHeight / 2)) {
                    currentPosLeft = 0;
                }
                if (Math.abs(currentPosRight) >= (rightStrip.scrollHeight / 2)) {
                    currentPosRight = 0;
                }

                if (window.innerWidth > 991) {
                    leftStrip.style.transform = `translateY(${currentPosLeft}px)`;
                    rightStrip.style.transform = `translateY(${currentPosRight}px)`;
                } else {
                    leftStrip.style.transform = 'none';
                    rightStrip.style.transform = 'none';
                }
            }
            animationFrameId = requestAnimationFrame(performAutoScroll);
        }

        animationFrameId = requestAnimationFrame(performAutoScroll);

        return () => {
            cancelAnimationFrame(animationFrameId);
            if (viewport) {
                viewport.removeEventListener('mouseenter', handleMouseEnter);
                viewport.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, []);

    return (
        <div>
            <Helmet>
                <title>Love & Family Photography in Hyderabad | KhultStudios</title>

                <meta
                    name="description"
                    content="Celebrate your special moments with KhultStudios. We offer professional couple, engagement, anniversary, family, lifestyle, and portrait photography in Hyderabad, creating timeless memories through creative storytelling."
                />

                <meta
                    name="keywords"
                    content="Love Photography Hyderabad, Couple Photography Hyderabad, Family Photography Hyderabad, Engagement Photography Hyderabad, Anniversary Photography Hyderabad, Couple Photoshoot Hyderabad, Family Portrait Photography, Lifestyle Photography Hyderabad, Romantic Photoshoot, Candid Couple Photography, Family Portraits, Outdoor Couple Photography, Indoor Family Photography, KhultStudios"
                />

                <meta name="robots" content="index, follow" />
                <meta name="author" content="KhultStudios" />

                <link
                    rel="canonical"
                    href="https://khultstudios.com/love-and-family-photography"
                />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta
                    property="og:url"
                    content="https://khultstudios.com/love-and-family-photography"
                />
                <meta
                    property="og:title"
                    content="Love & Family Photography in Hyderabad | KhultStudios"
                />
                <meta
                    property="og:description"
                    content="Professional couple, engagement, anniversary, and family photography in Hyderabad. Capture your love and family moments with creative portraits by KhultStudios."
                />
                <meta
                    property="og:image"
                    content="https://khultstudios.com/images/love-family-banner.jpg"
                />
                <meta property="og:site_name" content="KhultStudios" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="Love & Family Photography | KhultStudios"
                />
                <meta
                    name="twitter:description"
                    content="Beautiful couple, engagement, anniversary, and family photography sessions in Hyderabad."
                />
                <meta
                    name="twitter:image"
                    content="https://khultstudios.com/images/love-family-banner.jpg"
                />

                {/* Favicon */}
                <link rel="icon" href="https://khultstudios.com/favicon.ico" />
            </Helmet>
            {/* FULLSCREEN VIDEO VIEWPORT BACKGROUND ENGINE */}
            <div className="video-bg-viewport">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="global-html5-video"
                >
                    <source src="videos/family_cinematic.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                <div className="vignette-overlay-layer" />
            </div>

            {/* INNER APP CANVAS CONTAINER SECTION */}
            <div className="page-frame px-2 px-md-5 pb-5">
                <div className="landing-container position-relative overflow-hidden">
                    <div className="paper-texture" />
                    <div className="container-fluid h-100 p-4 p-lg-5 position-relative z-2">
                        <div className="row h-100 gy-5">

                            {/* Content Brief side description */}
                            <div className="col-lg-5 d-flex flex-column justify-content-center h-100 content-side">
                                <div className="invitation-text position-relative py-4">
                                    <h5 className="text-uppercase tracking-wider text-orange fw-bold mb-2">
                                        Love, Intimacy & Family Legacies
                                    </h5>
                                    <h1 className="display-3 text-uppercase main-title text-white mb-4">
                                        Freezing the Honest Chemistry of Connection
                                    </h1>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        Life moves fast, and families evolve in the quiet spaces between planned events. True visual legacy isn't about perfectly matching outfits standing stiffly in a row—it's the soft glance between partners, a toddler’s explosive laugh, and the tight embrace of generational roots.
                                    </p>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        At KhultStudio, we specialize in candid, light-filled connection portraiture. By ditching clinical studio stiffness for organic, play-driven prompt modules and golden hour settings, we harvest the raw emotional heartbeat of your family's history.
                                    </p>
                                    <a
                                        href="#details"
                                        className="btn btn-orange text-uppercase px-4 py-2 text-dark fw-bold tracking-wide btn-scroll"
                                    >
                                        Explore Connections
                                    </a>
                                </div>
                                <div className="social-footer d-flex gap-3 mt-2">
                                    <a href="#" className="social-icon"><i className="fab fa-facebook-f" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-instagram" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-pinterest" /></a>
                                </div>
                            </div>

                            {/* Infinite Slanted Ribbon Gallery Strip components */}
                            <div className="col-lg-7 position-relative photo-zone-height">
                                <div className="filmstrip-skew-box">
                                    <div
                                        className="vertical-scroll-viewport"
                                        ref={viewportRef}
                                    >
                                        <div className="row gx-4 h-100">
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon" ref={leftStripRef} style={{ display: 'none' }}>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1515934751635-c81c6bc9a2d8?q=80&w=500" alt="Intimate Couple Holding Hands Sunset Fields" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1595152772835-219674b2a8a6?q=80&w=500" alt="Spontaneous Joyful Family Candid Hug" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1518199266791-5375a83190b7?q=80&w=500" alt="Couples Close Up Stole Glance Connection" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1502086223501-7ea6ecd79368?q=80&w=500" alt="Children Running Across Golden Meadow" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1515934751635-c81c6bc9a2d8?q=80&w=500" alt="Intimate Couple Holding Hands Sunset Fields Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1595152772835-219674b2a8a6?q=80&w=500" alt="Spontaneous Joyful Family Candid Hug Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1518199266791-5375a83190b7?q=80&w=500" alt="Couples Close Up Stole Glance Connection Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1502086223501-7ea6ecd79368?q=80&w=500" alt="Children Running Across Golden Meadow Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon ribbon-offset" ref={rightStripRef}>
                                                    <div className="ribbon-cell">
                                                        <img src="/love-and-family/love-and-family-photography.jpg" alt="Wedding 5" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/love-and-family/love-and-family-photography2.jpg" alt="Wedding 6" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/love-and-family/love-and-family-photography3.jpg" alt="Wedding 7" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/love-and-family/love-and-family-photography4.jpg" alt="Wedding 8" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="/love-and-family/love-and-family-photography5.jpg" alt="Wedding 5 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/love-and-family/love-and-family-photography6.jpg" alt="Wedding 6 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/love-and-family/love-and-family-photography7.jpg" alt="Wedding 7 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/love-and-family/love-and-family-photography8.jpg" alt="Wedding 8 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* EXPANDED LOVE & RELATIONSHIPS SERVICES SECTION */}
            <section className="services-section py-5" id="details">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">✨ Specialized Relationship & Couples Portfolios</h2>
                    <p className="text-light-gray mt-3 text-center max-w-2xl mx-auto">
                        Honest connections grow over time. Our carefully developed lifestyle modules are engineered to handle dynamic human relationships, capturing every subtle gesture, secret smile, and spontaneous bear hug perfectly.
                    </p>
                    <div className="row g-4 mt-2">
                        {loveServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className={`service-card ${index === 0 ? 'active' : ''}`}>
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h3>{service.title}</h3>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* INTERPERSONAL FAMILY TRADITIONS */}
            <section className="wedding-services-section py-5">
                <div className="container">
                    <div className="text-center mb-5">
                        <h2 className="text-white">🎨 Generational Heritage & Archive Planning</h2>
                        <p className="text-light-gray mt-3 max-w-2xl mx-auto">
                            Beyond single snapshots lies your family’s expanding visual timeline. From yearly growth tracking frameworks to custom-tailored gallery installations, we preserve assets designed to protect your lineage's history.
                        </p>
                    </div>

                    <div className="row g-4 justify-content-center">
                        {heirloomServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* RELATIONSHIP OPERATIONAL COMPREHENSIVE MATRIX */}
            <section className="comprehensive-section py-5 bg-black-opacity-50">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">💼 Complete Session Support & Experience Deliverables</h2>
                    <div className="row g-4">
                        {relationshipDeliverables.map((service, index) => (
                            <div key={index} className="col-lg-4 col-md-6">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default LoveandFamily
